import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { formatEUR, formatDate } from '../../lib/format';
import {
  Modal,
  StatusPill,
  StatePill,
  labelCls,
  thCls,
  thNum,
  tdCls,
  tdNum,
  apiMessage,
  type Inv,
} from './collectionShared';

/** One invoice: what it asks, what settled it (or is still at the bank), and the letters that named it. */
interface Detail {
  invoice: Inv;
  payments: Array<{
    id: string;
    mode: string;
    amount: number;
    reference: string | null;
    date: string;
    number: string | null;
    state: string | null;
    kind: string;
    pending: boolean;
    reversed: boolean;
  }>;
  letters: Array<{
    id: string;
    number: string;
    step: string;
    label: string;
    date: string;
    status: string;
    auto: boolean;
    by: string | null;
  }>;
  disputes: Array<{
    reason: string;
    opened_at: string;
    opened_by: string | null;
    closed_at: string | null;
    outcome: string | null;
  }>;
}

export function InvoiceModal({
  invoiceId,
  onClose,
  onPay,
}: {
  invoiceId: string;
  onClose: () => void;
  onPay?: (customerId: string, invoiceId: string) => void;
}) {
  const { t } = useTranslation();
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    apiClient
      .get<Detail>(`/collection/invoices/${invoiceId}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(apiMessage(t, e)));
  }, [invoiceId, t]);
  const i = d?.invoice;
  return (
    <Modal
      title={i ? `${i.number} · ${i.customer?.name}` : '…'}
      lede={
        i
          ? `${t('collection.overview.dueOn', { date: formatDate(i.dueDate) })}${i.kind === 'RECAP' ? ` · ${t('collection.overview.bls', { count: i.deliveryNotes })}` : ''}`
          : undefined
      }
      onClose={onClose}
      wide
      footer={
        <>
          {i && onPay && i.s.remaining > 0.004 && (
            <Button className="mr-auto" onClick={() => onPay(i.customer!.id, i.id)}>
              {t('collection.overview.collect')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('collection.common.close')}
          </Button>
        </>
      }
    >
      {err && <p className="text-sm text-destructive">{err}</p>}
      {i && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <span className={labelCls}>{t('collection.col.amount')}</span>
              <b className="tabular-nums">{formatEUR(i.total)}</b>
              {i.gross !== i.total && (
                <div className="text-xs text-muted-foreground">
                  {t('collection.recaps.grossNote', { amount: formatEUR(i.gross) })}
                </div>
              )}
            </div>
            <div>
              <span className={labelCls}>{t('collection.col.settled')}</span>
              <b className="tabular-nums">{formatEUR(i.s.paid)}</b>
            </div>
            <div>
              <span className={labelCls}>{t('collection.col.remaining')}</span>
              <b className="tabular-nums">{formatEUR(i.s.remaining)}</b>
              {i.s.pending > 0 && (
                <div className="text-xs text-muted-foreground">
                  {t('collection.overview.pendingAmt', { amount: formatEUR(i.s.pending) })}
                </div>
              )}
            </div>
            <div>
              <span className={labelCls}>{t('collection.col.status')}</span>
              <StatusPill s={i.s} />
            </div>
          </div>
          {i.total <= 0.004 && i.kind === 'RECAP' && (
            <div className="rounded-md bg-destructive/[.10] px-3 py-2 text-sm font-medium text-destructive">
              {t('collection.recaps.zeroOne')}
            </div>
          )}
          <div>
            <span className={labelCls}>{t('collection.recaps.payments')}</span>
            {d.payments.length ? (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className={thCls}>{t('collection.col.date')}</th>
                      <th className={thCls}>{t('collection.col.mode')}</th>
                      <th className={thCls}>{t('collection.col.reference')}</th>
                      <th className={thNum}>{t('collection.col.amount')}</th>
                      <th className={thCls}>{t('collection.col.state')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.payments.map((p) => (
                      <tr key={`${p.id}-${p.kind}`} className="border-b last:border-b-0">
                        <td className={tdCls}>{formatDate(p.date)}</td>
                        <td className={tdCls}>
                          {t(`collection.mode.${p.mode}`, { defaultValue: p.mode })}
                          {p.kind === 'ON_ACCOUNT' && (
                            <div className="text-xs text-muted-foreground">
                              {t('collection.recaps.fromAdvance')}
                            </div>
                          )}
                        </td>
                        <td className={tdCls}>
                          <span className="font-mono text-xs">
                            {p.number ?? p.reference ?? '—'}
                          </span>
                        </td>
                        <td className={tdNum}>{formatEUR(p.amount)}</td>
                        <td className={tdCls}>
                          {p.reversed ? (
                            <StatePill state="REJETE" />
                          ) : p.state ? (
                            <StatePill state={p.state} />
                          ) : p.pending ? (
                            <Badge variant="warning">{t('collection.status.EN_COURS')}</Badge>
                          ) : (
                            <Badge variant="success">{t('collection.state.RECU')}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('collection.recaps.noPayment')}</p>
            )}
          </div>
          <div>
            <span className={labelCls}>{t('collection.recaps.letters')}</span>
            {d.letters.length ? (
              <ul className="grid gap-1 text-sm">
                {d.letters.map((l) => (
                  <li key={l.id}>
                    <span className="tabular-nums text-muted-foreground">{formatDate(l.date)}</span>{' '}
                    · <span className="font-mono text-xs">{l.number}</span> · {l.label}
                    {l.status === 'FAILED' && (
                      <span className="ml-1 font-semibold text-destructive">
                        {t('collection.dunning.notSent')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('collection.recaps.noLetter')}</p>
            )}
          </div>
          {d.disputes.length > 0 && (
            <div>
              <span className={labelCls}>{t('collection.recaps.disputes')}</span>
              {d.disputes.map((x, n) => (
                <p key={n} className="text-sm">
                  {formatDate(x.opened_at)} · {x.reason}
                  {x.closed_at && (
                    <span className="text-muted-foreground">
                      {' '}
                      → {formatDate(x.closed_at)} · {x.outcome}
                    </span>
                  )}
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
